import React, { useEffect, useState, useRef } from "react";
import Phone from "../assests/images/phone.svg";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import * as countryCodesList from "country-codes-list";
import { Link } from "react-router-dom";

// Define DeviceType enum
export const DeviceType = {
  ANDROID: "android",
  IOS: "ios",
};

// Generate country codes list from the package
const countryCodes = Object.entries(
  countryCodesList.customList(
    "countryCode",
    "{countryNameEn}|{countryCallingCode}"
  )
).map(([key, value]) => {
  const [countryName, code] = value.split("|");
  return {
    label: `${countryName} (+${code})`,
    value: `+${code}`,
  };
});

const DownloadSection = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    profession: "",
    otherProfession: "",
    industry: "",
    otherIndustry: "",
    organizationName: "",
    location: "",
    // deviceType: DeviceType.IOS, // Default to iOS
    deviceType: "", // No default selection
  });

  const [userType, setUserType] = useState("");
  const [deviceTypeError, setDeviceTypeError] = useState("");

  const defaultCountryCode =
    countryCodes.find(
      (code) =>
        code.value === "+1" && code.label === "United States of America (+1)"
    ) ||
    countryCodes.find((code) => code.value === "+1") ||
    countryCodes[0];
  const [selectedCountryCode, setSelectedCountryCode] =
    useState(defaultCountryCode);
  const [loading, setLoading] = useState(false);
  const [professionOptions, setProfessionOptions] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [isProfessionsLoading, setIsProfessionsLoading] = useState(false);
  const [isIndustriesLoading, setIsIndustriesLoading] = useState(false);
  const [isMessagingEnabled, setIsMessagingEnabled] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const locationRef = useRef(null);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyBki5ZWCB2g6atp2G1l8u14IQ7sEsZbvao",
    libraries: ["places"],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const submitForm = async (messagingEnabled) => {
    setLoading(true);

    const cleanedPhoneNumber = formData.phone.replace(/\s+/g, "").trim();
    const fullPhoneNumber = `${selectedCountryCode.value}${cleanedPhoneNumber}`;

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: fullPhoneNumber,
      location: formData.location,
      type: userType,
      isMessagingEnable: messagingEnabled,
      deviceType: formData.deviceType,
      ...(userType === "artist" && {
        profession:
          formData.profession === "Other"
            ? formData.otherProfession
            : formData.profession,
      }),
      ...(userType === "organization" && {
        organizationName: formData.organizationName,
        industry:
          formData.industry === "Other"
            ? formData.otherIndustry
            : formData.industry,
      }),
    };

    try {
      const response = await axios.post(
        "https://prodapi.pepliapp.com/api/v1/web/join-beta-program",
        payload
      );
      toast.success(response.data?.message);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        profession: "",
        otherProfession: "",
        industry: "",
        otherIndustry: "",
        organizationName: "",
        location: "",
        deviceType: DeviceType.IOS,
      });
      setUserType("");
      setSelectedCountryCode({
        label: "United States of America (+1)",
        value: "+1",
      });
      setIsMessagingEnabled(false);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Failed to join the beta program. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let hasError = false;

    if (!formData.deviceType) {
      setDeviceTypeError("Please select your device type.");
      hasError = true;
    } else {
      setDeviceTypeError("");
    }

    if (hasError) return;

    if (!isMessagingEnabled) {
      setShowPopup(true);
    } else {
      await submitForm(isMessagingEnabled);
    }
  };

  const handlePopupConfirm = async (enableMessaging) => {
    if (enableMessaging) {
      setIsMessagingEnabled(true);
    }
    setShowPopup(false);
    await submitForm(enableMessaging);
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      background: "white",
      color: "black",
      padding: "5px",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(48, 213, 200, 0.5)" : "none",
      borderColor: "gray-300",
      "&:hover": { borderColor: "#30D5C8" },
      borderRadius: "25px",
      width: "100%",
      minWidth: "100px",
      maxWidth: "100%",
      minHeight: "45px",
      textAlign: "left",
    }),
    menu: (base) => ({
      ...base,
      background: "white",
      borderRadius: "8px",
      boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected ? "#30D5C8" : isFocused ? "#e0f7f5" : "white",
      color: isSelected ? "white" : "black",
      "&:hover": { backgroundColor: "#30D5C8", color: "white" },
      cursor: "pointer",
      padding: "10px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "black",
    }),
  };

  const userOptions = [
    { value: "artist", label: "Artist / Performer / Creative" },
    {
      value: "organization",
      label: "Entertainment & Arts Organization / Business",
    },
    { value: "fan", label: "Audience Member / Fan" },
  ];

  useEffect(() => {
    if (userType === "artist") {
      const fetchProfessions = async () => {
        setIsProfessionsLoading(true);
        try {
          const response = await axios.post(
            "https://prodapi.pepliapp.com/api/v1/user/records",
            { type: "professions" }
          );
          const options = response.data?.data?.map((item) => ({
            label: item,
            value: item,
          }));
          setProfessionOptions(options || []);
        } catch (error) {
          console.error("Failed to fetch professions:", error);
        } finally {
          setIsProfessionsLoading(false);
        }
      };
      fetchProfessions();
    }

    if (userType === "organization") {
      const fetchIndustries = async () => {
        setIsIndustriesLoading(true);
        try {
          const response = await axios.post(
            "https://prodapi.pepliapp.com/api/v1/user/records",
            { type: "industry" }
          );
          const options = response.data?.data?.map((item) => ({
            label: item,
            value: item,
          }));
          setIndustryOptions(options || []);
        } catch (error) {
          console.error("Failed to fetch industries:", error);
        } finally {
          setIsIndustriesLoading(false);
        }
      };
      fetchIndustries();
    }
  }, [userType]);

  return (
    <section className="bg-light-dark pb-0 h-auto" id="download">
      <div className="flex flex-col medium:flex-row justify-center items-start mx-auto gap-12 px-4 lg:px-20 w-full max-w-full overflow-x-hidden">
        <div className="w-full medium:w-[50%] pt-4 pb-4">
          <div className="max-w-xl mx-auto bg-white p-8 rounded-2xl shadow-2xl">
            <h1 className="text-[#30D5C8] font-bold text-2xl mb-2 text-center">
              Join Our Beta Waitlist
            </h1>
            <p className="text-sm font-medium text-gray-600 text-center mb-6 max-w-[32rem] mx-auto">
              Be the first invited to experience our app for $0 FREE unlimited
              access. Be the first group in line to access gigs, join a
              community of creatives, and lay down the foundation of your
              presence in a new digital age within the arts & entertainment
              community.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <Select
                options={userOptions}
                value={
                  userOptions.find((opt) => opt.value === userType) || null
                }
                onChange={(selected) => setUserType(selected.value)}
                styles={customStyles}
                placeholder="Select your category"
              />

              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:border-[#30D5C8]"
                required
              />

              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:border-[#30D5C8]"
                required
              />

              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:border-[#30D5C8]"
                required
              />

              <div className="flex gap-3">
                <div className="w-36">
                  <Select
                    options={countryCodes}
                    value={selectedCountryCode}
                    onChange={setSelectedCountryCode}
                    styles={customStyles}
                  />
                </div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Mobile Number"
                  value={formData.phone}
                  onChange={handleChange}
                  className="flex-1 px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:border-[#30D5C8]"
                  required
                />
              </div>

              {userType === "artist" && (
                <>
                  <Select
                    isLoading={isProfessionsLoading}
                    options={professionOptions}
                    value={
                      professionOptions.find(
                        (opt) => opt.value === formData.profession
                      ) || null
                    }
                    onChange={(selected) =>
                      handleChange({
                        target: { name: "profession", value: selected.value },
                      })
                    }
                    styles={customStyles}
                    placeholder="Select Profession / Discipline"
                  />
                  {formData.profession === "Other" && (
                    <input
                      type="text"
                      name="otherProfession"
                      placeholder="Other Profession / Discipline"
                      value={formData.otherProfession}
                      onChange={handleChange}
                      className="w-full px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:border-[#30D5C8]"
                    />
                  )}
                </>
              )}

              {userType === "organization" && (
                <>
                  <input
                    type="text"
                    name="organizationName"
                    placeholder="Organization / Business Name"
                    value={formData.organizationName}
                    onChange={handleChange}
                    className="w-full px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:border-[#30D5C8]"
                  />
                  <Select
                    isLoading={isIndustriesLoading}
                    options={industryOptions}
                    value={
                      industryOptions.find(
                        (opt) => opt.value === formData.industry
                      ) || null
                    }
                    onChange={(selected) =>
                      handleChange({
                        target: { name: "industry", value: selected.value },
                      })
                    }
                    styles={customStyles}
                    placeholder="Select Industry"
                  />
                  {formData.industry === "Other" && (
                    <input
                      type="text"
                      name="otherIndustry"
                      placeholder="Other Industry"
                      value={formData.otherIndustry}
                      onChange={handleChange}
                      className="w-full px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:border-[#30D5C8]"
                    />
                  )}
                </>
              )}

              {isLoaded && (
                <Autocomplete
                  onLoad={(autocomplete) =>
                    (locationRef.current = autocomplete)
                  }
                  onPlaceChanged={() => {
                    if (!locationRef.current) return;
                    const place = locationRef.current.getPlace();
                    const formatted = place.formatted_address;
                    handleChange({
                      target: { name: "location", value: formatted },
                    });
                  }}
                >
                  <input
                    type="text"
                    name="location"
                    placeholder="Enter your city, state, or country"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:border-[#30D5C8]"
                    required
                  />
                </Autocomplete>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Choose Your Device Type{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="deviceType"
                      value={DeviceType.IOS}
                      checked={formData.deviceType === DeviceType.IOS}
                      onChange={handleChange}
                      className="h-5 w-5 border-gray-300 focus:ring-0 focus:ring-offset-0 appearance-none checked:bg-[#30D5C8] checked:border-[#30D5C8] rounded-full"
                      style={{
                        background:
                          formData.deviceType === DeviceType.IOS
                            ? "#30D5C8"
                            : "white",
                        border: "2px solid #d1d5db",
                      }}
                    />
                    <span className="text-sm text-gray-600">iOS</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="deviceType"
                      value={DeviceType.ANDROID}
                      checked={formData.deviceType === DeviceType.ANDROID}
                      onChange={handleChange}
                      className="h-5 w-5 border-gray-300 focus:ring-0 focus:ring-offset-0 appearance-none checked:bg-[#30D5C8] checked:border-[#30D5C8] rounded-full"
                      style={{
                        background:
                          formData.deviceType === DeviceType.ANDROID
                            ? "#30D5C8"
                            : "white",
                        border: "2px solid #d1d5db",
                      }}
                    />
                    <span className="text-sm text-gray-600">Android</span>
                  </label>
                </div>
                {deviceTypeError && (
                  <p className="text-red-500 text-sm mt-2">{deviceTypeError}</p>
                )}
              </div>

              <div className="mt-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    id="messagingEnabled"
                    checked={isMessagingEnabled}
                    onChange={(e) => setIsMessagingEnabled(e.target.checked)}
                    className="sr-only"
                  />
                  <span
                    className={`relative h-5 w-5 border border-gray-300 rounded flex-shrink-0 ${
                      isMessagingEnabled ? "bg-[#30D5C8] border-[#30D5C8]" : ""
                    }`}
                  >
                    {isMessagingEnabled && (
                      <svg
                        className="absolute h-3 w-3 text-white top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="text-sm text-gray-600">
                    I agree to receive SMS updates from Pepli. Message and data
                    rates may apply. Message frequency may vary. Reply STOP to
                    unsubscribe or HELP for help. See our{" "}
                    <Link
                      to="https://prodapi.pepliapp.com/api/v1/web/privacy-policy"
                      className="text-[#30D5C8]"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="https://prodapi.pepliapp.com/api/v1/web/terms-of-use"
                      className="text-[#30D5C8]"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </Link>
                    .
                  </span>
                </label>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#30D5C8] text-white py-3 rounded-lg font-medium hover:bg-[#28c6bb] transition"
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </form>
          </div>
        </div>
        <div className="flex flex-grow h-[100%]">
          <div className="hidden medium:flex flex-col flex-1">
            <img
              src={Phone}
              alt="Phone Screen"
              className="h-[100%] object-fill"
            />
          </div>
        </div>
      </div>

      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-[#30D5C8] font-bold text-xl mb-4 text-center">
              Stay Connected with Pepli!
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Stay up to date with the latest updates and exclusive offers by
              opting in to receive SMS updates from Pepli.
            </p>
            <div className="flex justify-between gap-4">
              <button
                onClick={() => handlePopupConfirm(false)}
                className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg font-medium hover:bg-gray-300 transition"
                disabled={loading}
              >
                Submit Anyway
              </button>
              <button
                onClick={() => handlePopupConfirm(true)}
                className="flex-1 bg-[#30D5C8] text-white py-2 rounded-lg font-medium hover:bg-[#28c6bb] transition"
                disabled={loading}
              >
                Enable SMS & Submit
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </section>
  );
};

export default DownloadSection;
